import { useEffect, useState } from "react";
import { Navigate, NavLink, Route, Routes, useNavigate } from "react-router-dom";
import Lenis from "lenis";
import { LogOut, LayoutDashboard, Trophy, Ticket, Users, BarChart3 } from "lucide-react";
import { AuthProvider, useAuth, api } from "./context/AuthContext";
import { LeagueProvider, useLeague } from "./context/LeagueContext";
import { Logo } from "./components/Logo";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Overview from "./pages/admin/Overview";
import Matches from "./pages/admin/Matches";
import Coupons from "./pages/admin/Coupons";
import Players from "./pages/admin/Players";
import Results from "./pages/admin/Results";
import { Toaster } from "sonner";
import "@/App.css";

const navItems = [
  { to: "/admin", end: true, label: "Översikt", icon: LayoutDashboard, testid: "nav-overview-link" },
  { to: "/admin/matcher", label: "Matcher", icon: Trophy, testid: "nav-matches-link" },
  { to: "/admin/spelare", label: "Spelare", icon: Users, testid: "nav-players-link" },
  { to: "/admin/resultat", label: "Resultat", icon: BarChart3, testid: "nav-results-link" },
];

function LeagueDisplay() {
  const { user } = useAuth();
  const { leagues, currentLeague, selectLeague, loadLeagues } = useLeague();
  const [showMenu, setShowMenu] = useState(false);
  const [deleting, setDeleting] = useState(null);

  if (!currentLeague || !user) return null;

  const isSuperadmin = user?.role === "superadmin";

  const handleDelete = async (league, e) => {
    e.stopPropagation();
    if (!confirm(`Är du säker på att du vill ta bort ligan "${league.name}"? All data (spelare, matcher, kuponger) raderas permanent.`)) {
      return;
    }
    setDeleting(league.id);
    try {
      await api.delete(`/leagues/${league.id}`);
      await loadLeagues();
      setShowMenu(false);
    } catch (err) {
      alert("Kunde inte ta bort ligan");
    } finally {
      setDeleting(null);
    }
  };

  if (!isSuperadmin) {
    return (
      <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2" data-testid="league-display">
        <span className="font-display font-semibold text-sm">{currentLeague.name}</span>
      </div>
    );
  }

  return (
    <div className="relative" data-testid="league-display">
      <button
        onClick={() => setShowMenu(!showMenu)}
        data-testid="league-selector-button"
        className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-slate-300 rounded-full px-4 py-2 text-sm font-medium transition-colors"
      >
        <span className="font-display font-semibold">{currentLeague.name}</span>
        <span className="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-semibold">SUPERADMIN</span>
      </button>

      {showMenu && (
        <div className="absolute right-0 mt-2 w-72 bg-white border border-slate-200 rounded-2xl shadow-xl py-2 z-50 max-h-96 overflow-y-auto" data-testid="league-dropdown">
          <p className="px-4 py-2 text-[11px] uppercase tracking-widest font-semibold text-slate-400">Alla ligor ({leagues.length})</p>
          {leagues.map((league) => (
            <div
              key={league.id}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors ${league.id === currentLeague.id ? "bg-blue-50" : ""}`}
            >
              <button
                onClick={() => { selectLeague(league); setShowMenu(false); }}
                className={`flex-1 text-left ${league.id === currentLeague.id ? "text-blue-700 font-semibold" : ""}`}
              >
                <div className="font-medium">{league.name}</div>
                <div className="text-[11px] text-slate-500">Admin: {league.admin_name}</div>
              </button>
              <button
                onClick={(e) => handleDelete(league, e)}
                disabled={deleting === league.id}
                className="text-slate-400 hover:text-red-600 transition-colors disabled:opacity-50 p-1"
                data-testid={`delete-league-${league.id}`}
                title="Ta bort liga"
              >
                {deleting === league.id ? (
                  <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
                  </svg>
                )}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminShell() {
  const { user, logout } = useAuth();
  const { currentLeague } = useLeague();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentLeague) {
      api.post(`/leagues/${currentLeague.id}/matches/settle-due`).catch(() => {});
    }
  }, [currentLeague]);

  if (user === null) {
    return (
      <div className="min-h-screen bg-[#FAFAFC] flex items-center justify-center" data-testid="admin-loading">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen bg-[#FAFAFC]">
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200" data-testid="admin-header">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <Logo size={30} />
            <div className="leading-tight">
              <p className="font-display font-bold text-sm">Fantasy Bet</p>
              <p className="text-[11px] text-slate-500">{user.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <LeagueDisplay />
            <span className="hidden sm:block text-xs text-slate-500" data-testid="admin-user-email">{user.email}</span>
            <button
              onClick={async () => { await logout(); navigate("/"); }}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 bg-white border border-slate-200 hover:border-slate-300 px-4 py-2 rounded-full transition-colors"
            >
              <LogOut size={14} /> Logga ut
            </button>
          </div>
        </div>
        {currentLeague && (
          <nav className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto flex gap-1 overflow-x-auto pb-0">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
                    isActive
                      ? "border-blue-500 text-blue-400"
                      : "border-transparent text-slate-300 hover:text-white"
                  }`
                }
              >
                <item.icon size={15} />
                {item.label}
              </NavLink>
            ))}
          </nav>
        )}
      </header>

      <main className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-10">
        {!currentLeague ? (
          <div className="text-center py-20">
            <p className="text-slate-500 mb-4">Du har inga ligor ännu.</p>
            <p className="text-sm text-slate-400">Skapa din första liga via menyn ovan.</p>
          </div>
        ) : (
          <Routes>
            <Route index element={<Overview />} />
            <Route path="matcher" element={<Matches />} />
            <Route path="spelare" element={<Players />} />
            <Route path="resultat" element={<Results />} />
          </Routes>
        )}
      </main>
    </div>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <LeagueProvider>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/admin/*" element={<AdminShell />} />
          </Routes>
        </LeagueProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
